import React, { useCallback, useMemo, useState } from 'react';
import AriaText from "../../../../../components/AriaComponents/AriaText";
import Icon from "../../../../../components/Icon";
import { usePolyglot } from "../../../../../context/Polyglot";
import styles from '../styles.module.scss';
import { StyledButton } from "../../../../../components/StyledComponents";
import { useConversationContext } from '../../../../../context/ChatContext/contextConvo';
import { useTypedDispatch, useTypedSelector } from '../../../../../redux/hooks';
import { ChatMemberFull, selectSortedMembersOnChat } from '../../../../../redux/slices/chatNSlice';
import { nebulaChatApi, useChatChatter } from '../../../../../redux/services/nebulaChat';
import EntityIcon from '../../../../../components/icons/EntityIcon';
import { selectAuthUserByUuid, selectCurrentUserId, setActiveChat, startCall } from '../../../../../redux/slices';
import StyledDropdown from '../../../../../components/StyledComponents/StyledDropdown';


export type MemberOption =
    {
        label: string,
        value: 'chat' | 'call' | 'remove',
        onClick: () => void
    };

type UserPerms = {
    isAdmin: boolean,
    isCreator: boolean
}

const GroupMember: React.FC<{ member: ChatMemberFull, localUserPerms: UserPerms }> = ({ member, localUserPerms }) => {
    const { conversation } = useConversationContext();
    const userUuid = useTypedSelector(selectCurrentUserId);

    const accountUser = useTypedSelector(state => selectAuthUserByUuid(state, member.member));

    const chatter = useChatChatter({ userUuid: member.member });

    const [kickWarning, setKickWarning] = useState<boolean>(false);

    const dispatch = useTypedDispatch()

    const { t } = usePolyglot();

    const [channelAction] = nebulaChatApi.endpoints.channelActionN.useMutation();

    const handleKick = useCallback(() => {
        if (!localUserPerms.isAdmin && !localUserPerms.isCreator) {
            return;
        }
        if (kickWarning) {
            channelAction({
                action: 'remove_members',
                channel_uuid: conversation.uuid,
                users: [member.member]
            });
        } else {
            setKickWarning(true)
        }
    }, [kickWarning]);

    const options = useMemo(() => {
        const opts: MemberOption[] = [
            {
                label: t("actions.chat", 1),
                value: 'chat',
                onClick: () => dispatch(setActiveChat(member.member))
            },
        ];

        if (accountUser) {
            opts.push(
                {
                    label: t("actions.call", 1),
                    value: 'call',
                    onClick: () => dispatch(startCall(accountUser.extension.toString(), accountUser.nickname)),
                }
            );
        }

        if ((localUserPerms.isAdmin || localUserPerms.isCreator) && member.member !== userUuid && !member.is_creator) {
            opts.push(
                {
                    label: kickWarning ? t("phrases.are_you_sure") : t("actions.remove_from_group"),
                    value: 'remove',
                    onClick: () => handleKick(),
                })
        }

        return opts;
    }, [accountUser, kickWarning]);

    return (
        <div
            className={styles.group_member}
            onBlur={(e) => {
                if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) {
                    setKickWarning(false)
                }
            }}
        >
            <div className={styles.group_member_icon}>
                <EntityIcon
                    type='user'
                    details={{
                        uuid: chatter.uuid,
                        display_name: chatter.name,
                        avatar_key: chatter.file_key
                    }}
                    size={24}
                />
            </div>
            <p>
                {chatter.name}
                {(member.is_creator && ` (${t("terms.creator", 1)})`) || (member.is_admin && ` (${t("terms.admin", 1)})`)}
            </p>
            <div
                className={styles.group_member_option}
            >
                <StyledDropdown
                    options={options}
                    component={(props) =>
                        <StyledButton
                            key={`GCMO_${props.value}`}
                            buttonStyle='text'
                            onClick={() => props.onClick()}
                            context={props.value === 'remove' ? 'danger' : undefined}
                        >
                            <p>
                                {props.label}
                            </p>
                        </StyledButton>
                    }
                    iconOnly
                    direction='right'
                />
            </div>
        </div>
    )
}

const ChannelParticipants: React.FC = () => {
    const { conversation } = useConversationContext();
    const userUuid = useTypedSelector(selectCurrentUserId);

    const participants = useTypedSelector(state => selectSortedMembersOnChat(state, conversation.uuid));

    const { t } = usePolyglot();

    const [groupMembersList, setGroupMembersList] = useState<boolean>(false);
    const [showGroupMembers, setShowGroupMembers] = useState<boolean>(false);

    const userPerms = useMemo(() => {
        const localMember = participants.find((m) => m.member === userUuid);

        return {
            isAdmin: localMember?.is_admin ?? false,
            isCreator: localMember?.is_creator ?? false,
        }
    }, [])

    return (
        <div className={styles.channel_members}>
            <StyledButton
                buttonStyle='text'
                onClick={() => setGroupMembersList(!groupMembersList)}
                size='medium'
            >
                <AriaText text={groupMembersList ? t("actions.show_less") : t("actions.show_all")} />
                <p>
                    {t("phrases.%count_participants", { count: participants.length })}
                </p>
                <Icon name={groupMembersList ? 'arrowDown' : 'arrowRight'} width={20} height={20} />
            </StyledButton>
            {groupMembersList && <>
                {participants.slice(0, showGroupMembers ? participants.length : 10).map(member => (
                    <GroupMember
                        key={member.member}
                        member={member}
                        localUserPerms={userPerms}
                    />
                ))}
                {participants.length > 10 ? (
                    <StyledButton
                        buttonStyle='text'
                        size='medium'
                        onClick={() => setShowGroupMembers(!showGroupMembers)}
                    >
                        <p>
                            {showGroupMembers ? t("actions.show_less") : t("actions.show_all")}
                        </p>
                        <Icon name={showGroupMembers ? 'arrowUp' : 'arrowDown'} ariaHidden width={22} height={22} />
                    </StyledButton>
                ) : null}
            </>
            }
        </div>
    );
};

export default ChannelParticipants;
